<script setup lang="ts">
import { computed, watch, getCurrentInstance, ref } from "vue";
import { Firebase } from "@/utils/firebase-utility";
import NavigationLink from "./navigation-link.vue";
import AdminDropdownItem from "@/component/AdminDropdownItem.vue";
import AdminDropdown from "@/component/AdminDropdown.vue";
import AdminAccordionSimple from "@/component/AdminAccordionSimple.vue";
import AdminAccordionGroup from "@/component/AdminAccordionGroup.vue";
import { useUiTheme } from "./use-ui-theme.vue";
import { useRouter } from "vue-router";
import LogoSection from "@/component-bill-purser/logo-section.vue";

const brandingConfig = BRANDING_CONFIG;

const router = useRouter();
const path = computed(() => router.currentRoute.value.path);

const logout = () => {
  Firebase.logout();
  location.reload();
};

const uiTheme = useUiTheme();

// アコーディオングループのrefを取得
const accordionGroupRef = ref();

function getAccordionIdByPath(path: string) {
  if (path.startsWith("/quotations")) return "quotation";
  if (path.startsWith("/invoices")) return "invoice";
  if (path.startsWith("/delivery_notes")) return "delivery-note";
  return null;
}

watch(path, (newPath) => {
  const id = getAccordionIdByPath(newPath);
  // forceOpenを呼ぶ
  accordionGroupRef.value?.forceOpen?.(id);
});
</script>

<template>
  <div class="bg-menu-base flex h-[100dvh] min-h-[100svh] w-full max-w-xs p-3 pb-0">
    <ul class="safearea-bottom flex w-full flex-col">
      <div class="-m-2">
        <AdminDropdown placement="left" button-type="none">
          <template #button>
            <div
              class="bg-base border-base-button hover:bg-base-button-hover z-20 flex h-8 w-8 cursor-pointer items-center justify-center rounded-sm border transition-all duration-150"
            >
              <i v-if="uiTheme.currentTheme.value === 'light'" class="fa-solid fa-sun text-main-text" />
              <i v-if="uiTheme.currentTheme.value === 'dark'" class="fa-solid fa-moon text-main-text" />
            </div>
          </template>
          <template v-slot:items="dropdown">
            <AdminDropdownItem
              @onClick="uiTheme.setLight"
              @onClose="dropdown.onClose"
              :class="{ 'text-main': uiTheme.themeMode.value === 'light' }"
            >
              <div class="flex w-full items-center justify-items-start gap-2">
                <div class="flex w-4 items-center justify-center">
                  <i class="fa-solid fa-sun" />
                </div>
                <span>ライトモード</span>
              </div>
            </AdminDropdownItem>
            <AdminDropdownItem
              @onClick="uiTheme.setDark"
              @onClose="dropdown.onClose"
              :class="{ 'text-main': uiTheme.themeMode.value === 'dark' }"
            >
              <div class="flex w-full items-center justify-items-start gap-2">
                <div class="flex w-4 items-center justify-center">
                  <i class="fa-solid fa-moon" />
                </div>
                <span>ダークモード</span>
              </div>
            </AdminDropdownItem>
            <AdminDropdownItem
              @onClick="uiTheme.setAuto"
              @onClose="dropdown.onClose"
              :class="{ 'text-main': uiTheme.themeMode.value === 'auto' }"
            >
              <div class="flex w-full items-center justify-items-start gap-2">
                <div class="flex w-4 items-center justify-center">
                  <i class="fa-solid fa-desktop" />
                </div>
                <span>自動設定</span>
              </div>
            </AdminDropdownItem>
          </template>
        </AdminDropdown>
      </div>

      <li class="mt-6">
        <RouterLink to="/" class="flex flex-col items-center justify-center">
          <LogoSection class="px-2" />
        </RouterLink>
      </li>

      <div class="bg-base-button my-3 h-0.5" />
      <div
        class="flex flex-col gap-1"
        v-if="
          Firebase.organization.value?.plan === 'paid' ||
          Firebase.organization.value?.plan === 'trial' ||
          Firebase.organization.value?.plan === 'readonly'
        "
      >
        <div class="grid grid-cols-2 gap-2">
          <NavigationLink direction="vertical" icon="house" link="/" :selected="path === '/'">ホーム</NavigationLink>
          <NavigationLink
            direction="vertical"
            icon="address-book"
            link="/clients"
            :selected="path.startsWith('/clients')"
          >
            取引先一覧
          </NavigationLink>
        </div>
        <!-- <NavigationLink icon="file-invoice" link="/invoices">請求書管理</NavigationLink> -->
        <AdminAccordionGroup ref="accordionGroupRef">
          <AdminAccordionSimple
            id="quotation"
            header-direction="reverse"
            header-class="hover:bg-bar transition-all duration-150 px-3"
            class="-mx-3 mt-4"
          >
            <template v-slot:header>
              <div class="flex w-full flex-row items-center gap-1.5">
                <h1 class="my-1 font-bold">見積書</h1>
                <i class="fa-solid fa-quote-left text-main text-lg" />
              </div>
            </template>
            <template v-slot:contents>
              <div class="mx-3 mb-4 mt-2 flex flex-col gap-2">
                <NavigationLink
                  v-if="Firebase.getCreatePaperPermission('quotation')"
                  icon="square-plus"
                  link="/quotations/list/create/edit"
                  :selected="path.startsWith('/quotations/list/create/edit')"
                >
                  新しい見積書を作成
                </NavigationLink>
                <NavigationLink
                  icon="file-pen"
                  link="/quotations/list"
                  :selected="path.startsWith('/quotations/list') && !path.startsWith('/quotations/list/create/edit')"
                >
                  作成中の見積書一覧
                </NavigationLink>
                <NavigationLink
                  icon="clock-rotate-left"
                  link="/quotations/history"
                  :selected="path.startsWith('/quotations/history')"
                >
                  送信済の見積書履歴
                </NavigationLink>
              </div>
            </template>
          </AdminAccordionSimple>
          <AdminAccordionSimple
            id="invoice"
            opened
            header-direction="reverse"
            header-class="hover:bg-bar transition-all duration-150 px-3"
            class="-mx-3"
          >
            <template v-slot:header>
              <div class="flex w-full flex-row items-center gap-1.5">
                <h1 class="my-1 font-bold">請求書</h1>
                <i class="fa-solid fa-file-invoice-dollar text-main text-lg" />
              </div>
            </template>
            <template v-slot:contents>
              <div class="mx-3 mb-4 mt-2 flex flex-col gap-2">
                <NavigationLink
                  v-if="Firebase.getCreatePaperPermission('invoice')"
                  icon="square-plus"
                  link="/invoices/list/create/edit"
                  :selected="path.startsWith('/invoices/list/create/edit')"
                >
                  新しい請求書を作成
                </NavigationLink>
                <NavigationLink
                  icon="file-pen"
                  link="/invoices/list"
                  :selected="path.startsWith('/invoices/list') && !path.startsWith('/invoices/list/create/edit')"
                >
                  作成中の請求書一覧
                </NavigationLink>
                <NavigationLink
                  icon="clock-rotate-left"
                  link="/invoices/history"
                  :selected="path.startsWith('/invoices/history')"
                >
                  送信済の請求書履歴
                </NavigationLink>
              </div>
            </template>
          </AdminAccordionSimple>
          <AdminAccordionSimple
            id="delivery-note"
            header-direction="reverse"
            header-class="hover:bg-bar transition-all duration-150 px-3"
            class="-mx-3"
          >
            <template v-slot:header>
              <div class="flex w-full flex-row items-center gap-1.5">
                <h1 class="my-1 font-bold">納品書</h1>
                <i class="fa-solid fa-truck text-main text-lg" />
              </div>
            </template>
            <template v-slot:contents>
              <div class="mx-3 mb-4 mt-2 flex flex-col gap-2">
                <NavigationLink
                  v-if="Firebase.getCreatePaperPermission('deliveryNote')"
                  icon="square-plus"
                  link="/delivery_notes/list/create/edit"
                  :selected="path.startsWith('/delivery_notes/list/create/edit')"
                >
                  新しい納品書を作成
                </NavigationLink>
                <NavigationLink
                  icon="file-pen"
                  link="/delivery_notes/list"
                  :selected="
                    path.startsWith('/delivery_notes/list') && !path.startsWith('/delivery_notes/list/create/edit')
                  "
                >
                  作成中の納品書一覧
                </NavigationLink>
                <NavigationLink
                  icon="clock-rotate-left"
                  link="/delivery_notes/history"
                  :selected="path.startsWith('/delivery_notes/history')"
                >
                  送信済の納品書履歴
                </NavigationLink>
              </div>
            </template>
          </AdminAccordionSimple>
        </AdminAccordionGroup>
      </div>

      <div class="mt-auto"></div>
      <div
        class="mb-3 grid grid-cols-2 gap-2"
        v-if="
          Firebase.organization.value?.plan === 'paid' ||
          Firebase.organization.value?.plan === 'trial' ||
          Firebase.organization.value?.plan === 'readonly'
        "
      >
        <NavigationLink direction="vertical" icon="gear" link="/configs" :selected="path.startsWith('/configs')">
          設定
        </NavigationLink>
        <a
          :href="brandingConfig.manualUrl"
          class="hover:bg-main-light bg-base-button flex items-center rounded-md transition-all duration-150"
          :class="['grow flex-col justify-center gap-0.5 py-1.5 pt-2 text-xs']"
          target="_blank"
        >
          <i class="fa-sharp fa-solid fa-circle-question text-xl"></i>
          <div>操作マニュアル</div>
        </a>
      </div>

      <div class="border-outline -mx-2 flex h-24 flex-col justify-between border-t p-3 text-xs">
        <span class="text-main-text' flex items-center gap-2">
          <i :class="'fa-solid fa-building'"></i>
          {{ Firebase.organization.value?.name }}
        </span>
        <span class="text-main-text' flex items-center gap-2">
          <i :class="'fa-solid fa-user'"></i>
          {{ Firebase.organizationUser.value?.name }}
        </span>
        <div class="flex flex-col items-end gap-2">
          <span
            class="text-main hover:text-main-hover flex cursor-pointer items-center justify-center gap-1 rounded p-1.5"
            @click="logout"
          >
            <div class="">ログアウト</div>
            <i :class="'fa-solid fa-arrow-right-from-bracket'" />
          </span>
        </div>
      </div>
    </ul>
  </div>
</template>

<style scoped lang="scss"></style>
